/**
 * Site API Client Module
 *
 * This module provides functions for making HTTP requests to the site configuration API.
 * It is implemented as a separate module from other application code per Requirement 2.1.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6
 */

import type { SiteConfigResponse, ListKeysResponse, CreateKeyResponse } from '../models/apiModels';
import { track } from '../../analytics';
import type { ApiErrorAttributes } from '../../analytics';

/**
 * Configuration for the Site API client.
 * Contains the API host URL and a function to obtain the access token.
 *
 * Requirements: 2.3, 2.4, 8.2
 */
export interface SiteApiClientConfig {
  readonly apiHost: string;
  readonly getAccessToken: () => Promise<string | null>;
}

/**
 * Error thrown when a site is not found (HTTP 404).
 *
 * Requirement 6.1: IF the API returns a 404 status, THEN THE Site_Entry_View
 * SHALL display "Site not found" error message
 */
export class SiteNotFoundError extends Error {
  constructor(public readonly siteId: string) {
    super(`Site not found: ${siteId}`);
    this.name = 'SiteNotFoundError';
  }
}

/**
 * Error thrown when the API returns an error response (non-404).
 *
 * Requirement 2.6: WHEN the API returns an error response, THE Site_API_Client
 * SHALL throw an error with the HTTP status code
 */
export class SiteApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'SiteApiError';
  }
}

/**
 * Tracks an API error event with the error type.
 * 
 * Requirement 9.1: WHEN an API error occurs, THE Analytics_Service SHALL track
 * "Pocket Admin API Error" with ErrorType attribute
 */
function trackApiError(errorType: string): void {
  const attributes: ApiErrorAttributes = { ErrorType: errorType };
  track('Pocket Admin API Error', attributes);
}

/**
 * Fetches the site configuration from the backend API.
 *
 * Requirement 2.2: WHEN loading a site configuration, THE Site_API_Client SHALL
 * call the endpoint `{apiHost}/site/{siteId}/config`
 *
 * Requirement 2.3: THE Site_API_Client SHALL include the Keycloak access token
 * in the Authorization header for all requests
 *
 * Requirement 2.4: THE Site_API_Client SHALL obtain the access token via the
 * getAccessToken function from the useAuth hook
 *
 * Requirement 2.5: WHEN the API returns a successful response, THE Site_API_Client
 * SHALL return the parsed JSON response
 *
 * Requirement 2.6: WHEN the API returns an error response, THE Site_API_Client
 * SHALL throw an error with the HTTP status code
 *
 * @param siteId - The unique identifier of the site to fetch
 * @param config - Configuration containing apiHost and getAccessToken function
 * @returns Promise resolving to the site configuration response
 * @throws Error if no access token is available
 * @throws SiteNotFoundError if the site is not found (HTTP 404)
 * @throws SiteApiError for other HTTP error responses
 */
export async function fetchSiteConfig(
  siteId: string,
  config: SiteApiClientConfig
): Promise<SiteConfigResponse> {
  // Requirement 2.4: Obtain access token via getAccessToken function
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  // Requirement 2.2: Call endpoint {apiHost}/site/{siteId}/config
  const response = await fetch(
    `${config.apiHost}/site/${siteId}/config`,
    {
      headers: {
        // Requirement 2.3: Include Keycloak access token in Authorization header
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  // Requirement 2.6: Throw error with HTTP status code for error responses
  if (response.status === 404) {
    trackApiError('SiteNotFound');
    throw new SiteNotFoundError(siteId);
  }

  if (!response.ok) {
    trackApiError(`HTTP_${response.status}`);
    throw new SiteApiError(response.status, `API error: ${response.status}`);
  }

  // Requirement 2.5: Return parsed JSON response for successful responses
  return response.json();
}

/**
 * Fetches the list of valid keys for a site.
 *
 * Requirement 1.1: THE Site_Key_API_Client SHALL provide a function to list keys for a site
 *
 * Requirement 1.2: WHEN listing keys, THE Site_Key_API_Client SHALL call the endpoint
 * `GET {apiHost}/admin-api/site/{siteId}/key`
 *
 * Requirement 1.3: THE Site_Key_API_Client SHALL include the Keycloak access token
 * in the Authorization header
 *
 * Requirement 1.4: WHEN the API returns a successful response, THE Site_Key_API_Client
 * SHALL return the parsed ListKeysResponse containing the keys array
 *
 * Requirement 1.5: WHEN the API returns an error response, THE Site_Key_API_Client
 * SHALL throw an error with the HTTP status code
 *
 * @param siteId - The unique identifier of the site
 * @param config - Configuration containing apiHost and getAccessToken function
 * @returns Promise resolving to ListKeysResponse
 * @throws Error if no access token is available
 * @throws SiteApiError for HTTP error responses
 */
export async function fetchSiteKeys(
  siteId: string,
  config: SiteApiClientConfig
): Promise<ListKeysResponse> {
  // Requirement 1.3: Obtain access token via getAccessToken function
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  // Requirement 1.2: Call endpoint GET {apiHost}/admin-api/site/{siteId}/key
  const response = await fetch(
    `${config.apiHost}/admin-api/site/${siteId}/key`,
    {
      headers: {
        // Requirement 1.3: Include Keycloak access token in Authorization header
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  // Requirement 1.5: Throw error with HTTP status code for error responses
  if (!response.ok) {
    trackApiError(`HTTP_${response.status}`);
    throw new SiteApiError(response.status, `API error: ${response.status}`);
  }

  // Requirement 1.4: Return parsed ListKeysResponse for successful responses
  return response.json();
}

/**
 * Creates a new key for a site.
 *
 * Requirement 2.1: THE Site_Key_API_Client SHALL provide a function to create a key for a site
 *
 * Requirement 2.2: WHEN creating a key, THE Site_Key_API_Client SHALL call the endpoint
 * `POST {apiHost}/admin-api/site/{siteId}/key`
 *
 * Requirement 2.3: THE Site_Key_API_Client SHALL include the Keycloak access token
 * in the Authorization header
 *
 * Requirement 2.4: WHEN the API returns a successful response, THE Site_Key_API_Client
 * SHALL return the parsed CreateKeyResponse containing the new key
 *
 * Requirement 2.5: WHEN the API returns an error response, THE Site_Key_API_Client
 * SHALL throw an error with the HTTP status code
 *
 * @param siteId - The unique identifier of the site
 * @param config - Configuration containing apiHost and getAccessToken function
 * @returns Promise resolving to CreateKeyResponse
 * @throws Error if no access token is available
 * @throws SiteApiError for HTTP error responses
 */
export async function createSiteKey(
  siteId: string,
  config: SiteApiClientConfig
): Promise<CreateKeyResponse> {
  // Requirement 2.3: Obtain access token via getAccessToken function
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  // Requirement 2.2: Call endpoint POST {apiHost}/admin-api/site/{siteId}/key
  const response = await fetch(
    `${config.apiHost}/admin-api/site/${siteId}/key`,
    {
      method: 'POST',
      headers: {
        // Requirement 2.3: Include Keycloak access token in Authorization header
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  // Requirement 2.5: Throw error with HTTP status code for error responses
  if (!response.ok) {
    trackApiError(`HTTP_${response.status}`);
    throw new SiteApiError(response.status, `API error: ${response.status}`);
  }

  // Requirement 2.4: Return parsed CreateKeyResponse for successful responses
  return response.json();
}
